import { apiFetch } from '@/lib/api-client';
import { UserRole } from '@/components/authnz/types/rbac';

export interface CreateSubscriptionPayload {
  productId: string;
  userId: string;
  userEmails: string[];
  userRole: UserRole;
  startDate: Date;
  currentPeriodStart: Date;
  currentPeriodEnd: Date | null;
  status: 'active';
  isLifetime: boolean;
}

export interface UpdateProductPayload {
  name: string;
  description: string;
  userRole: UserRole;
  isLifetime: boolean;
  priceAmount: number | null;
  priceCurrency: string | null;
}

export interface StripeProductsSyncReport {
  products: { added: number; updated: number; deactivated: number };
  prices: { added: number; updated: number; deactivated: number };
}

export interface StripeSubscriptionsSyncReport {
  subscriptions: { added: number; updated: number; canceled: number; skippedLocal: number };
  rolesGranted: number;
  rolesRevoked: number;
}

export async function createSubscriptionApi(payload: CreateSubscriptionPayload): Promise<void> {
  await apiFetch('/subscriptions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateProductApi(productId: string, payload: UpdateProductPayload): Promise<void> {
  await apiFetch(`/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function syncProductsFromStripeApi(
  userRole: UserRole
): Promise<{ report?: StripeProductsSyncReport }> {
  return apiFetch<{ report?: StripeProductsSyncReport }>('/products/sync-stripe', {
    method: 'POST',
    body: JSON.stringify({ userRole }),
  });
}

export async function deleteProductApi(productId: string): Promise<void> {
  await apiFetch(`/products/${productId}`, { method: 'DELETE' });
}

export interface ReconcilePreview {
  inactiveUsersToRegainRoles: number;
}

/**
 * Preview of the full Stripe reconcile. Throws when the server cannot answer;
 * callers must not start the reconcile without a successful preview (AUD-05).
 */
export async function fetchReconcilePreviewApi(): Promise<ReconcilePreview> {
  const result = await apiFetch<{ success?: boolean; inactiveUsersToRegainRoles?: number }>(
    '/subscriptions/reconcile-preview',
  );
  if (typeof result?.inactiveUsersToRegainRoles !== 'number') {
    throw new Error('Reconcile preview returned no data');
  }
  return { inactiveUsersToRegainRoles: result.inactiveUsersToRegainRoles };
}

export async function syncSubscriptionsFromStripeApi(options?: {
  dryRun?: boolean;
  confirm?: boolean;
}): Promise<{ report?: StripeSubscriptionsSyncReport }> {
  return apiFetch<{ report?: StripeSubscriptionsSyncReport }>('/subscriptions/sync-stripe', {
    method: 'POST',
    body: JSON.stringify(options ?? {}),
  });
}

export async function deleteSubscriptionApi(subscriptionId: string): Promise<void> {
  await apiFetch(`/subscriptions/${subscriptionId}`, { method: 'DELETE' });
}
